import React, { useState, useEffect } from 'react';
import { 
  Box, Button, Typography, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Paper, IconButton, 
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { useAuth, API_BASE_URL, UPLOADS_BASE_URL } from '../../context/AuthContext';

const parseItalianDateToISO = (dateStr) => {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;

  const months = {
    'gennaio': '01', 'febbraio': '02', 'marzo': '03', 'aprile': '04',
    'maggio': '05', 'giugno': '06', 'luglio': '07', 'agosto': '08',
    'settembre': '09', 'ottobre': '10', 'novembre': '11', 'dicembre': '12'
  };

  const parts = dateStr.toLowerCase().split(' ');
  let day = '', month = '', year = '';

  for (const part of parts) {
    if (months[part]) {
      month = months[part];
    } else if (/^\d{4}$/.test(part)) {
      year = part;
    } else if (/^\d{1,2}$/.test(part)) {
      day = part.padStart(2, '0');
    }
  }

  if (year && month && day) {
    return `${year}-${month}-${day}`;
  }
  return '';
};

const formatDateToItalian = (dateStr) => {
  if (!dateStr) return '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;

  const [year, monthNum, dayNum] = dateStr.split('-');
  const months = [
    'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
    'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'
  ];
  const month = months[parseInt(monthNum, 10) - 1];
  return `${parseInt(dayNum, 10)} ${month} ${year}`;
};

export default function EventsAdmin({ isPastView = false }) {
  const [events, setEvents] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '', event_date: '', event_time: '', text_content: '', image_url: '', is_past: isPastView
  });
  
  const { user } = useAuth();

  const handleUploadLocandina = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const token = localStorage.getItem('adminToken');
    const formDataUpload = new FormData();
    formDataUpload.append('image', file);

    setUploading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/upload_event_image.php`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });
      const data = await response.json();
      if (response.ok && data.url) {
        setFormData(prev => ({ ...prev, image_url: data.url }));
      } else {
        alert(data.error || "Errore durante il caricamento della locandina.");
      }
    } catch (error) {
      console.error("Locandina upload error:", error);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const fetchEvents = async () => {
    try {
      const url = `${API_BASE_URL}/events.php?is_past=${isPastView ? '1' : '0'}`;
      const response = await fetch(url);
      const data = await response.json();
      if(Array.isArray(data)) setEvents(data);
    } catch (error) {
      console.error("Errore fetch events:", error);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [isPastView]);

  const handleOpenForm = (event = null) => {
    if (event) {
      setEditingId(event.id);
      const isoDate = parseItalianDateToISO(event.event_date);
      setFormData({
        title: event.title || '',
        event_date: isoDate,
        event_time: event.event_time || '',
        text_content: event.text_content || '',
        image_url: event.image_url || '',
        is_past: Boolean(Number(event.is_past))
      });
    } else {
      setEditingId(null);
      setFormData({
        title: '', event_date: '', event_time: '', text_content: '', image_url: '', is_past: isPastView
      });
    }
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleSave = async () => {
    const token = localStorage.getItem('adminToken');
    const method = editingId ? 'PUT' : 'POST';
    const formattedDate = formData.event_date ? formatDateToItalian(formData.event_date) : '';
    const payload = editingId ? { ...formData, event_date: formattedDate, id: editingId } : { ...formData, event_date: formattedDate };

    try {
      const response = await fetch(`${API_BASE_URL}/events.php`, {
        method: method,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        handleCloseForm();
        fetchEvents();
      } else {
        alert("Errore durante il salvataggio.");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleToggleArchive = async (row) => {
    const token = localStorage.getItem('adminToken');
    const newIsPast = row.is_past == 1 ? 0 : 1;
    const actionName = newIsPast ? "spostare in Eventi Passati" : "ripristinare nelle News";
    
    if (!window.confirm(`Sei sicuro di voler ${actionName} questo elemento?`)) return;

    try {
      const response = await fetch(`${API_BASE_URL}/events.php`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...row, is_past: newIsPast })
      });
      
      if (response.ok) {
        fetchEvents();
      } else {
        alert("Errore durante lo spostamento.");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo elemento?")) return;
    const token = localStorage.getItem('adminToken');

    try {
      const response = await fetch(`${API_BASE_URL}/events.php`, {
        method: 'DELETE',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id })
      });
      
      if (response.ok) {
        fetchEvents();
      } else {
        alert("Errore durante l'eliminazione.");
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          {isPastView ? 'Archivio Eventi Passati' : 'Gestione News'}
        </Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpenForm()}>
          {isPastView ? '+ Aggiungi Evento Passato' : '+ Nuova News'}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Data</TableCell>
              <TableCell>Titolo</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: 140 }}>Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((row) => (
              <TableRow key={row.id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.event_date}</TableCell>
                <TableCell>{row.title}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: 140 }}>
                  <Box sx={{ display: 'inline-flex', flexWrap: 'nowrap' }}>
                    <Tooltip title={isPastView ? "Ripristina nelle News" : "Sposta negli Eventi Passati"}>
                      <IconButton color="info" onClick={() => handleToggleArchive(row)}>
                        {isPastView ? <UnarchiveIcon /> : <ArchiveIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Modifica">
                      <IconButton color="primary" onClick={() => handleOpenForm(row)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Elimina">
                      <IconButton color="error" onClick={() => handleDelete(row.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {events.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">Nessun elemento presente.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modale per Aggiunta/Modifica */}
      <Dialog open={openForm} onClose={handleCloseForm} fullWidth maxWidth="md">
        <DialogTitle>{editingId ? 'Modifica' : (isPastView ? 'Nuovo Evento Passato' : 'Nuova News')}</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            
            <TextField
              margin="normal" fullWidth required
              label="Titolo"
              name="title"
              value={formData.title}
              onChange={handleChange}
            />
            <TextField
              margin="normal" fullWidth required
              label="Data dell'evento"
              type="date"
              name="event_date"
              value={formData.event_date}
              onChange={handleChange}
              onClick={(e) => {
                try {
                  if (typeof e.target.showPicker === 'function') {
                    e.target.showPicker();
                  }
                } catch (err) {}
              }}
              InputLabelProps={{ shrink: true }}
              helperText="Seleziona la data dal calendario (verrà mostrata nel sito es. '26 luglio 2026')"
            />
            <TextField
              margin="normal" fullWidth
              label="Orario dell'evento"
              type="time"
              name="event_time"
              value={formData.event_time}
              onChange={handleChange}
              onClick={(e) => {
                try {
                  if (typeof e.target.showPicker === 'function') {
                    e.target.showPicker();
                  }
                } catch (err) {}
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
            />
            <TextField
              margin="normal" fullWidth required
              label="Testo della News / Evento"
              name="text_content"
              multiline rows={6}
              value={formData.text_content}
              onChange={handleChange}
            />
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Locandina dell'evento (opzionale):
              </Typography>

              {formData.image_url ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#f9f9f9' }}>
                  <img 
                    src={formData.image_url.startsWith('http') || formData.image_url.startsWith('../') ? formData.image_url : `${UPLOADS_BASE_URL}/${formData.image_url.replace(/^\//, '')}`} 
                    alt="Locandina" 
                    style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
                  />
                  <Typography variant="caption" sx={{ flexGrow: 1, wordBreak: 'break-all' }}>
                    {formData.image_url}
                  </Typography>
                  <Button 
                    size="small" 
                    color="error" 
                    onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                  >
                    Rimuovi
                  </Button>
                </Box>
              ) : (
                <Tooltip title="Seleziona un'immagine dal tuo computer per allegare una locandina">
                  <Button 
                    variant="outlined" 
                    startIcon={<UploadFileIcon />}
                    component="label"
                    disabled={uploading}
                  >
                    {uploading ? 'Caricamento locandina...' : '+ Carica Locandina'}
                    <input 
                      type="file" 
                      accept="image/*"
                      hidden
                      onChange={handleUploadLocandina} 
                    />
                  </Button>
                </Tooltip>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm}>Annulla</Button>
          <Button onClick={handleSave} variant="contained">Salva</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
